// Fase 6 — storefront publico. A diferencia de los paneles, esta app no
// consulta Supabase: consume el contrato de JSON estatico generado por el
// flujo de publicacion.
//
// Fase 9 (Optimizacion) — cambios en esta capa (ver ADR-0010):
// 1. Resolucion de version: antes de leer cualquier archivo de contenido,
//    se lee `{slug}/version.json` (pequeno, sin cache) y se arma la ruta
//    real `{slug}/v/{version}/archivo` — el mismo mecanismo de swap
//    atomico que usa `publicar_tienda` para publicar. Memorizado en una
//    promesa a nivel de modulo: aunque varias llamadas pidan archivos en
//    paralelo, `version.json` se descarga una sola vez por carga de pagina.
// 2. `productos/index.json` ahora es el "listado ligero" (seccion 2.3): se
//    arma con `cargarIndiceProductos`, que sigue la paginacion generada
//    por `publicar_tienda` en vez de asumir un unico archivo.
// 3. El detalle completo de un producto (galeria completa, variantes,
//    descripcion larga) ya no viaja con el listado — se pide bajo demanda
//    con `cargarDetalleProducto`, cacheado en memoria por slug.

// ADR-0012: ninguna de estas tres variables tiene fallback. El storefront
// habla con Supabase Storage (desarrollo) o con Cloudflare (producción)
// según lo que diga VITE_CDN_TIENDAS_BASE — el código nunca decide por su
// cuenta a partir de NODE_ENV ni de ningún otro indicio, y no hay una ruta
// relativa "razonable" a la que degradar si falta: usarla escondería un
// .env mal configurado sirviendo contenido de otro origen sin avisar.
function variableEntornoObligatoria(nombre) {
  const valor = import.meta.env[nombre];
  if (!valor) {
    throw new Error(
      `[datos-publicados] Falta ${nombre} en el entorno. Completa tu .env a partir de ` +
        '.env.example (sección "Dominio de la plataforma y origen del storefront").',
    );
  }
  return valor;
}

const BASE_TIENDAS = variableEntornoObligatoria('VITE_CDN_TIENDAS_BASE');
const DOMINIO_PLATAFORMA = variableEntornoObligatoria('VITE_DOMINIO_PLATAFORMA');
const TIENDA_LOCAL = variableEntornoObligatoria('VITE_TIENDA_DEMO');

/**
 * @typedef {object} TiendaPublicada
 * @property {object} empresaJson
 * @property {object} homeJson
 * @property {object} categoriasJson
 * @property {object} productosJson
 */

export function resolverSlugTienda() {
  if (typeof window !== 'undefined' && window.__STORE_TENANT__?.slug) {
    return window.__STORE_TENANT__.slug;
  }

  const tiendaQuery = new URLSearchParams(window.location.search).get('tienda');
  if (tiendaQuery) return tiendaQuery;

  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host === '') return TIENDA_LOCAL;

  if (host.endsWith(`.${DOMINIO_PLATAFORMA}`)) {
    return host.slice(0, -DOMINIO_PLATAFORMA.length - 1);
  }

  return host.replace(/^www\./, '');
}

// Una sola promesa de version por carga de pagina (una tienda por
// subdominio, nunca cambia durante la sesion de navegacion salvo un
// refresh). Evita pedir `version.json` una vez por cada archivo.
let promesaVersion = null;

/**
 * @param {string} slugTienda
 * @returns {Promise<string>}
 */
function resolverVersionPublicada(slugTienda) {
  if (promesaVersion) return promesaVersion;

  promesaVersion = fetch(`${BASE_TIENDAS}/${slugTienda}/version.json`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  }).then(async (respuesta) => {
    if (!respuesta.ok) {
      throw new Error(`No se pudo resolver la tienda publicada (${respuesta.status})`);
    }
    const datos = await respuesta.json();
    if (!datos?.version) throw new Error('version.json invalido: falta "version".');
    return String(datos.version);
  });

  // Si falla, se limpia la memoizacion para permitir reintentar en una
  // proxima navegacion/recarga en vez de quedar con un error "pegado".
  promesaVersion.catch(() => {
    promesaVersion = null;
  });

  return promesaVersion;
}

/**
 * Lee un archivo del contrato publicado de una tienda, resolviendo primero
 * la version vigente. Los archivos de contenido viven bajo una ruta
 * versionada e inmutable (`v/{version}/...`), cacheable agresivamente por
 * el navegador y por la CDN — ver `publicar_tienda` (Fase 9).
 *
 * @param {string} slugTienda
 * @param {string} archivo
 */
export async function leerJsonTienda(slugTienda, archivo) {
  const version = await resolverVersionPublicada(slugTienda);
  const respuesta = await fetch(`${BASE_TIENDAS}/${slugTienda}/v/${version}/${archivo}`, {
    headers: { Accept: 'application/json' },
  });

  if (!respuesta.ok) {
    throw new Error(`No se pudo cargar ${archivo} (${respuesta.status})`);
  }

  return respuesta.json();
}

/**
 * Arma el listado ligero de productos siguiendo la paginacion que generó
 * `publicar_tienda` (seccion 2.3: "paginacion por archivo si el catalogo es
 * grande"). La primera pagina ya indica cuantas hay en total; el resto se
 * pide en paralelo. Para la enorme mayoria de tiendas (catalogos chicos)
 * esto es exactamente 1 request, igual que antes de la Fase 9.
 *
 * @param {string} slugTienda
 */
async function cargarIndiceProductos(slugTienda) {
  const primeraPagina = await leerJsonTienda(slugTienda, 'productos/index.json');
  const totalPaginas = primeraPagina.paginas_totales ?? 1;

  if (totalPaginas <= 1) {
    return primeraPagina.productos ?? [];
  }

  const restantes = await Promise.all(
    Array.from({ length: totalPaginas - 1 }, (_, indice) =>
      leerJsonTienda(slugTienda, `productos/index-${indice + 2}.json`),
    ),
  );

  return [primeraPagina, ...restantes].flatMap((pagina) => pagina.productos ?? []);
}

// Cache en memoria de fichas completas de producto — varias vistas pueden
// pedir el mismo slug durante una sesion (producto -> volver -> producto de
// nuevo, o el carrito resolviendo variantes de items ya vistos). Al vivir
// en una ruta versionada e inmutable, el contenido nunca puede quedar
// obsoleto dentro de la misma carga de pagina.
const cacheDetalleProducto = new Map();

/**
 * Carga la ficha completa de un producto (galeria completa, variantes,
 * descripcion larga) bajo demanda — seccion 2.3: "productos/{slug}.json —
 * ficha detallada de cada producto (carga bajo demanda, solo cuando el
 * usuario entra al detalle)". Se usa tanto desde la vista de producto como
 * desde el carrito (para resolver precio/atributos exactos de una
 * variante elegida).
 *
 * @param {string} slugTienda
 * @param {string} slugProducto
 * @returns {Promise<object|null>} `null` si no se pudo cargar (el producto
 *   ya no existe, la publicacion no lo incluyo, etc.) — nunca lanza, para
 *   que el llamador pueda seguir mostrando el dato liviano que ya tiene.
 */
export function cargarDetalleProducto(slugTienda, slugProducto) {
  const clave = `${slugTienda}/${slugProducto}`;
  if (cacheDetalleProducto.has(clave)) return cacheDetalleProducto.get(clave);

  const promesa = leerJsonTienda(slugTienda, `productos/${slugProducto}.json`)
    .then((datos) => datos.producto ?? null)
    .catch(() => null);

  cacheDetalleProducto.set(clave, promesa);
  return promesa;
}

export async function cargarTiendaPublicada() {
  const slugTienda = resolverSlugTienda();
  const [empresaJson, homeJson, categoriasJson, productosLigeros] = await Promise.all([
    leerJsonTienda(slugTienda, 'empresa.json'),
    leerJsonTienda(slugTienda, 'home.json'),
    leerJsonTienda(slugTienda, 'categorias.json'),
    cargarIndiceProductos(slugTienda),
  ]);

  return {
    slugTienda,
    empresa: empresaJson.empresa,
    perfil: empresaJson.perfil ?? {},
    // Fase 10 (Tematización visual) — `color_primario`/`color_secundario`
    // pueden venir `null` (tienda sin tema configurado); lo resuelve
    // `tema.js#aplicarTemaVisual`, no esta capa de datos.
    tema: empresaJson.tema ?? { color_primario: null, color_secundario: null },
    // Config del Editor de tienda (Parte 2 / Motor de temas). `null` si la
    // publicacion aun no la incluye o la tienda no uso el editor; lo
    // normaliza `motortema.js`.
    editor: empresaJson.editor ?? null,
    seo: empresaJson.seo ?? {},
    bloques: (homeJson.bloques ?? [])
      .filter((bloque) => bloque.estado === 'activo')
      .sort((a, b) => a.orden - b.orden),
    banners: homeJson.banners ?? [],
    categorias: (categoriasJson.categorias ?? []).filter(
      (categoria) => categoria.estado !== 'oculta',
    ),
    productos: (productosLigeros ?? []).filter((producto) => producto.estado === 'publicado'),
  };
}
